import React, { useMemo } from 'react';
import AutoSizeTitle from '../../components/autoSizeTitle/AutoSizeTitle';
import IndividualInformHeader from '../../components/individualInformHeader/individualInformHeader';
import ResumeBarChartSelfcare from '../../graphics/selfcare/resumeBarChartSelfcare';
import {
    SELFCARE_DIMENSION_DEFS,
    getDimensionsEntries,
    getGlobalNarrative,
    getVisibleSelfcareGlobalScore,
    groupDimensionsByRisk,
} from './selfcareReportLogic';
import './pageResultsByDimensionSelfcare.css';

const DIMENSION_GUIDE_SUMMARY = {
    // Textos anclados al documento guía (frases cortas, sin inventar contenido clínico)
    conciencia: 'Atención al riesgo: te anticipas y no trabajas “en automático”.',
    manejoemocional: 'Reacciones bajo presión: mantienes calma y piensas antes de actuar.',
    bienestarfisico: 'Fatiga: energía y concentración sostienen la ejecución segura.',
    disciplina: 'Cumplir procedimientos: seguir el paso a paso te protege.',
    responsabilidad: 'Seguridad colectiva: reportas, asumes tus tareas y ayudas al equipo.',
    comunicacion: 'Reporte de riesgos: hablar a tiempo evita riesgos ocultos.',
    proposito: 'Compromiso: trabajar con cuidado y calidad reduce descuidos.',
};

const normalizeKey = (key) =>
    String(key || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/\s+/g, '');

const NO_PROTECTORS_MESSAGE = 'Ninguna dimensión se encuentra en Riesgo Bajo.';

const DIMENSION_LEGEND = SELFCARE_DIMENSION_DEFS
    .slice()
    .sort((a, b) => a.radarOrder - b.radarOrder)
    .map((dimension) => ({
        code: dimension.code,
        label: dimension.reportTitle.replace(/^D\d\s+/, ''),
    }));

const PageResultsByDimensionSelfcare = ({ selectedEvaluation, individualResult, orgLogo, pageNumber }) => {
    const backgroundStyle = {
        backgroundImage: `url('/informs/pagina.png')`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left top',
        backgroundSize: '100% auto',
    };

    const dimensionsEntries = useMemo(() => {
        return getDimensionsEntries(individualResult, { order: 'report' });
    }, [individualResult]);

    const chartDimensionsEntries = useMemo(() => {
        return getDimensionsEntries(individualResult, { order: 'radar' });
    }, [individualResult]);

    const dimensionsObj = useMemo(() => {
        return chartDimensionsEntries.reduce((acc, dim) => {
            acc[dim.key] = dim;
            return acc;
        }, {});
    }, [chartDimensionsEntries]);

    const grouped = useMemo(() => {
        const { protectors, attention, high, critical } = groupDimensionsByRisk(individualResult);

        const names = (arr) => arr.map((d) => d.title).join(', ');

        return {
            protectors,
            toStrengthen: attention,
            high,
            critical,
            names: {
                protectors: names(protectors),
                toStrengthen: names(attention),
                high: names(high),
                critical: names(critical),
            },
        };
    }, [individualResult]);

    const dimensionNotes = useMemo(() => {
        const notes = [];
        dimensionsEntries.forEach((dim) => {
            const k = normalizeKey(dim.title);
            const note = DIMENSION_GUIDE_SUMMARY[k];
            if (note) notes.push({ title: dim.title, note });
        });
        return notes;
    }, [dimensionsEntries]);
    const visibleGlobalScore = getVisibleSelfcareGlobalScore(individualResult);

    return (
        <>
            <div className="pdf-page internal-page">
                <div className="internal-page-content" style={backgroundStyle}>
                    <AutoSizeTitle text={selectedEvaluation?.evl_title} />

                    <div className="internal-page-info internal-page-info-individual">
                        <IndividualInformHeader individualResult={individualResult} orgLogo={orgLogo} />

                        <div className="selfcare-dim-title">Resultados por dimensión</div>

                        <div className="selfcare-dim-intro">
                            La gráfica de dimensiones presenta el resultado de los siete factores clave del autocuidado,
                            evaluados en una escala de 0 a 100. Cada dimensión representa una condición que influye
                            directamente en la seguridad personal y en la probabilidad de cometer errores o enfrentar
                            situaciones de riesgo.
                            <br />
                            <br />
                            Analizar las dimensiones de forma separada permite comprender qué aspectos están protegiendo
                            tu seguridad y cuáles están generando mayor vulnerabilidad, más allá del resultado global.
                        </div>

                        <div className="selfcare-dim-chart-box">
                            <div className="selfcare-dim-chart">
                                <ResumeBarChartSelfcare data={dimensionsObj} yAxisWidth={30} />
                            </div>
                            <div className="selfcare-dimension-code-legend selfcare-dimension-code-legend--results">
                                {DIMENSION_LEGEND.map((dimension) => (
                                    <div key={dimension.code} className="selfcare-dimension-code-legend-item">
                                        <span className="selfcare-dimension-code-legend-code">{dimension.code}</span>
                                        <span className="selfcare-dimension-code-legend-text">{dimension.label}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="selfcare-dim-chart-caption">
                                Esta gráfica permite identificar qué factores están fortaleciendo tu seguridad y cuáles la están
                                debilitando.
                            </div>

                            <div className="selfcare-dim-chart-caption selfcare-dim-chart-caption--narrative">
                                {getGlobalNarrative(visibleGlobalScore)}
                            </div>

                            <div className="selfcare-dim-bullets">
                                {grouped.protectors.length > 0 && (
                                    <div>• Protectoras (Riesgo Bajo): {grouped.names.protectors}.</div>
                                )}
                                {grouped.toStrengthen.length > 0 && (
                                    <div>• Atención (Riesgo Moderado): {grouped.names.toStrengthen}.</div>
                                )}
                                {grouped.high.length > 0 && (
                                    <div>• Riesgo Alto: {grouped.names.high}.</div>
                                )}
                                {grouped.critical.length > 0 && (
                                    <div>• Riesgo Crítico: {grouped.names.critical}.</div>
                                )}
                            </div>
                        </div>

                        <div className="selfcare-dim-footnote">
                            Esta gráfica muestra los factores que fortalecen tu autocuidado y aquellos que requieren mayor
                            atención. Las dimensiones en nivel crítico son prioridad de intervención para reducir la exposición
                            conductual al error.
                        </div>
                    </div>

                    <div className="internal-page-number">{pageNumber}</div>
                </div>
            </div>

            <div className="pdf-page internal-page">
                <div className="internal-page-content" style={backgroundStyle}>
                    <AutoSizeTitle text={selectedEvaluation?.evl_title} />

                    <div className="internal-page-info internal-page-info-individual">
                        <IndividualInformHeader individualResult={individualResult} orgLogo={orgLogo} />

                        <div className="selfcare-dim-title">Caracterización de dimensiones</div>

                        <div className="selfcare-dim-sections">
                            <div className="selfcare-dim-section">
                                <div className="selfcare-dim-section-title">Dimensiones protectoras — Riesgo Bajo (≥ 80)</div>
                                <div className="selfcare-dim-section-sub">{grouped.names.protectors || NO_PROTECTORS_MESSAGE}</div>
                                <div className="selfcare-dim-section-body">
                                    Estas dimensiones se ubican en condición protectora. Indican prácticas que ayudan a operar de forma más segura y consistente.
                                </div>
                            </div>

                            {grouped.toStrengthen.length > 0 && (
                                <div className="selfcare-dim-section">
                                    <div className="selfcare-dim-section-title">Dimensiones de atención — Riesgo Moderado (65–79)</div>
                                    <div className="selfcare-dim-section-sub">{grouped.names.toStrengthen}</div>
                                    <div className="selfcare-dim-section-body">
                                        Estas dimensiones se ubican en una zona intermedia. En algunos momentos funcionan bien y en otros pueden generar vulnerabilidad. El foco es aplicar pausas, verificación y hábitos simples para estabilizarlas.
                                    </div>
                                </div>
                            )}

                            {grouped.high.length > 0 && (
                                <div className="selfcare-dim-section">
                                    <div className="selfcare-dim-section-title">Dimensiones en Riesgo Alto (50–64)</div>
                                    <div className="selfcare-dim-section-sub">{grouped.names.high}</div>
                                    <div className="selfcare-dim-section-body">
                                        Estas dimensiones se ubican en riesgo alto. Representan vulnerabilidades activas que requieren atención prioritaria y acciones concretas de refuerzo: pausas, verificación adicional y acompañamiento en tareas críticas.
                                    </div>
                                </div>
                            )}

                            {grouped.critical.length > 0 && (
                                <div className="selfcare-dim-section">
                                    <div className="selfcare-dim-section-title">Dimensiones en Riesgo Crítico (&lt; 50)</div>
                                    <div className="selfcare-dim-section-sub">{grouped.names.critical}</div>
                                    <div className="selfcare-dim-section-body">
                                        Estas dimensiones se ubican en nivel Crítico. Este nivel implica alta exposición combinada cuando coincide con otras vulnerabilidades; por eso se priorizan acciones inmediatas: pausas, doble verificación, pedir apoyo, y evitar tareas críticas en solitario.
                                    </div>
                                </div>
                            )}
                        </div>

                        {dimensionNotes.length > 0 && (
                            <div className="selfcare-dim-notes selfcare-dim-notes--page">
                                {dimensionNotes.slice(0, 4).map((n) => (
                                    <div key={n.title} className="selfcare-dim-note">
                                        <span className="selfcare-dim-note-title">{n.title}:</span> {n.note}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="internal-page-number">{pageNumber + 1}</div>
                </div>
            </div>
        </>
    );
};

export default PageResultsByDimensionSelfcare;
